import { useState } from 'react';
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2, Wrench, Truck, AlertTriangle, Calendar, Info } from "lucide-react";
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import toast from 'react-hot-toast';

interface CreateOTModalProps {
    open: boolean;
    onClose: () => void;
    vehiculo: {
        id: string;
        placa: string;
        km_actual: number;
        rutinas: any[];
    };
    onSuccess?: () => void;
}

type ModalTab = 'preventivo' | 'correctivo';

export function CreateOTModal({ open, onClose, vehiculo, onSuccess }: CreateOTModalProps) {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<ModalTab>('preventivo');
    const [selectedRoutines, setSelectedRoutines] = useState<number[]>([]);

    // Form states for Corrective OT
    const [correctiveForm, setCorrectiveForm] = useState({
        tipo: 'CORRECTIVO' as 'CORRECTIVO' | 'EMERGENCIA',
        prioridad: 'MEDIA' as 'BAJA' | 'MEDIA' | 'ALTA' | 'URGENTE',
        fecha_programada: new Date().toLocaleDateString('sv-SE'), // sv-SE uses YYYY-MM-DD format based on local time
        observaciones: ''
    });

    const toggleRoutine = (id: number) => {
        if (selectedRoutines.includes(id)) {
            setSelectedRoutines(selectedRoutines.filter(r => r !== id));
        } else {
            setSelectedRoutines([...selectedRoutines, id]);
        }
    };


    const [expandedDetails, setExpandedDetails] = useState<number[]>([]);

    const toggleDetails = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        if (expandedDetails.includes(id)) {
            setExpandedDetails(expandedDetails.filter(d => d !== id));
        } else {
            setExpandedDetails([...expandedDetails, id]);
        }
    };

    // ... (rest of the code)

    const handleCreateMultipleRoutines = async () => {
        setLoading(true);
        try {
            await maintenanceService.createOTFromMultipleRoutines({
                vehiculo_id: vehiculo.id,
                rutinas_ids: selectedRoutines,
                kilometraje_actual: vehiculo.km_actual || 0
            });
            toast.success(`Orden de Trabajo generada con ${selectedRoutines.length} rutinas`);
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating OT:', error);
            toast.error("Error al generar la Orden de Trabajo");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCorrectiveOT = async () => {
        setLoading(true);
        try {
            await maintenanceService.createCustomOT({
                vehiculo_id: vehiculo.id,
                tipo: correctiveForm.tipo as any,
                prioridad: correctiveForm.prioridad,
                fecha_programada: correctiveForm.fecha_programada,
                kilometraje_inicio: vehiculo.km_actual || 0,
                observaciones: correctiveForm.observaciones
            });
            toast.success("Orden de Trabajo Correctiva creada");
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating corrective OT:', error);
            toast.error("Error al crear la OT correctiva");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-950 rounded-2xl shadow-2xl w-full max-w-xl flex flex-col border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200 overflow-hidden">
                {/* Header */}
                <div className="p-6 pb-4">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-2xl">
                                <Wrench className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-slate-900 dark:text-white">Generar Orden</h2>
                                <p className="text-sm font-medium text-slate-500">Vehículo: <span className="text-blue-600 font-bold">{vehiculo.placa}</span></p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400">
                            <Truck className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tabs Modernas */}
                    <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-xl gap-1">
                        <button
                            onClick={() => setActiveTab('preventivo')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'preventivo'
                                ? 'bg-white dark:bg-slate-800 text-blue-600 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            <Calendar className="w-4 h-4" />
                            Preventivo (Plan)
                        </button>
                        <button
                            onClick={() => setActiveTab('correctivo')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'correctivo'
                                ? 'bg-white dark:bg-slate-800 text-red-600 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            <AlertTriangle className="w-4 h-4" />
                            Correctivo / Ajuste
                        </button>
                    </div>
                </div>

                <div className="p-6 pt-0 space-y-4 max-h-[60vh] overflow-y-auto">
                    {/* Info de Kilometraje */}
                    <div className="bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Info className="w-4 h-4 text-blue-500" />
                            <span className="text-sm font-bold text-blue-700 dark:text-blue-300">Km actual</span>
                        </div>
                        <span className="text-lg font-black text-blue-900 dark:text-blue-100">{(vehiculo.km_actual || 0).toLocaleString()} km</span>
                    </div>

                    {activeTab === 'preventivo' ? (
                        <div className="space-y-3">
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Selecciona una o más rutinas:</p>
                            <div className="grid gap-2">
                                {vehiculo.rutinas.length > 0 ? (
                                    vehiculo.rutinas.map((rutina, idx) => {
                                        const isSelected = selectedRoutines.includes(rutina.id);
                                        return (
                                            <button
                                                key={idx}
                                                disabled={loading}
                                                onClick={() => toggleRoutine(rutina.id)}
                                                className={`w-full group p-4 rounded-2xl border-2 text-left transition-all flex items-center justify-between hover:translate-x-1 ${isSelected
                                                    ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500'
                                                    : rutina.estado === 'CRITICO'
                                                        ? 'border-red-100 bg-red-50/30 hover:bg-red-50 hover:border-red-300'
                                                        : rutina.estado === 'ALERTA'
                                                            ? 'border-orange-100 bg-orange-50/30 hover:bg-orange-50 hover:border-orange-300'
                                                            : 'border-slate-50 bg-slate-50/30 hover:bg-slate-50 hover:border-blue-200'
                                                    }`}
                                            >
                                                <div className="flex flex-col flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`font-bold ${isSelected ? 'text-blue-700' : 'text-slate-900 dark:text-slate-100'}`}>
                                                            {rutina.nombre}
                                                        </span>
                                                        <Badge variant="outline" className={`text-[9px] uppercase font-black px-1.5 py-0 ${rutina.estado === 'CRITICO' ? 'bg-red-100 text-red-700 border-red-200' :
                                                            rutina.estado === 'ALERTA' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                                                'bg-green-100 text-green-700 border-green-200'
                                                            }`}>
                                                            {rutina.estado}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-xs text-slate-500 font-medium">
                                                            {rutina.km_restantes > 0
                                                                ? `A realizar en ${rutina.km_restantes.toLocaleString()} km`
                                                                : `Vencido hace ${Math.abs(rutina.km_restantes).toLocaleString()} km`}
                                                        </span>

                                                        {rutina.items && rutina.items.length > 0 && (
                                                            <div
                                                                onClick={(e) => toggleDetails(e, rutina.id)}
                                                                className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded cursor-pointer transition-colors"
                                                            >
                                                                {expandedDetails.includes(rutina.id) ? 'Ocultar Items' : `Ver ${rutina.items.length} Items`}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Lista de Items (Colapsable) */}
                                                    {expandedDetails.includes(rutina.id) && (
                                                        <div className="bg-white/80 dark:bg-slate-900/50 rounded-lg p-3 text-xs border border-slate-200 dark:border-slate-700 space-y-1 shadow-sm mt-1 animate-in slide-in-from-top-1 duration-200">
                                                            <p className="font-bold text-slate-400 text-[10px] uppercase mb-2 border-b border-slate-100 pb-1">Items incluídos:</p>
                                                            {rutina.items && rutina.items.length > 0 ? (
                                                                rutina.items.map((item: any, i: number) => (
                                                                    <div key={i} className="flex justify-between items-center text-slate-600 dark:text-slate-400 py-0.5">
                                                                        <span className="font-medium truncate pr-2">• {item.nombre}</span>
                                                                        <span className="font-mono bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap text-slate-500 border border-slate-200 dark:border-slate-700">
                                                                            {item.cantidad} {item.unidad_medida}
                                                                        </span>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <span className="text-slate-400 italic">Sin items configurados</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={`h-6 w-6 ml-3 rounded-full border-2 flex items-center justify-center transition-colors shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-200 bg-white'
                                                    }`}>
                                                    {isSelected && <Wrench className="w-3 h-3 text-white" />}
                                                </div>
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="text-center py-8 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                                        <p className="text-sm text-slate-500 font-medium">No hay rutinas activas</p>
                                    </div>
                                )}
                            </div>

                            {selectedRoutines.length > 0 && (
                                <div className="pt-2 animate-in slide-in-from-bottom-2">
                                    <Button
                                        onClick={handleCreateMultipleRoutines}
                                        disabled={loading}
                                        className="w-full py-6 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-xl shadow-lg shadow-blue-200 dark:shadow-none transition-all active:scale-95"
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Wrench className="w-5 h-5 mr-2" />}
                                        CREAR ORDEN ({selectedRoutines.length} Rutinas)
                                    </Button>
                                    <p className="text-[10px] text-center text-slate-400 mt-2">
                                        Se generará una única Orden de Trabajo combinando los ítems de las rutinas seleccionadas.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-5 animate-in slide-in-from-right-2 duration-300">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-400 uppercase ml-1">Tipo de Incidencia</label>
                                    <select
                                        value={correctiveForm.tipo}
                                        onChange={(e) => setCorrectiveForm({ ...correctiveForm, tipo: e.target.value as any })}
                                        className="w-full bg-slate-50 dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-bold focus:border-red-400 outline-none transition-all"
                                    >
                                        <option value="CORRECTIVO">Correctivo / Falla</option>
                                        <option value="EMERGENCIA">Emergencia / Choque</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-400 uppercase ml-1">Prioridad</label>
                                    <select
                                        value={correctiveForm.prioridad}
                                        onChange={(e) => setCorrectiveForm({ ...correctiveForm, prioridad: e.target.value as any })}
                                        className="w-full bg-slate-50 dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-bold focus:border-red-400 outline-none transition-all"
                                    >
                                        <option value="BAJA">Baja</option>
                                        <option value="MEDIA">Media</option>
                                        <option value="ALTA">Alta</option>
                                        <option value="URGENTE">Urgente</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-400 uppercase ml-1">Fecha de Ingreso</label>
                                <input
                                    type="date"
                                    value={correctiveForm.fecha_programada}
                                    onChange={(e) => setCorrectiveForm({ ...correctiveForm, fecha_programada: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-bold focus:border-red-400 outline-none"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-400 uppercase ml-1">Observaciones / Detalles</label>
                                <textarea
                                    placeholder="Describe la falla, ajuste o motivo de la orden..."
                                    rows={3}
                                    value={correctiveForm.observaciones}
                                    onChange={(e) => setCorrectiveForm({ ...correctiveForm, observaciones: e.target.value })}
                                    className="w-full bg-slate-50 dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-xl px-4 py-3 text-sm font-medium focus:border-red-400 outline-none resize-none"
                                />
                            </div>

                            <Button
                                onClick={handleCreateCorrectiveOT}
                                disabled={loading}
                                className="w-full py-6 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl shadow-lg shadow-red-200 dark:shadow-none transition-all active:scale-95"
                            >
                                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Wrench className="w-5 h-5 mr-2" />}
                                CREAR ORDEN CORRECTIVA
                            </Button>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-slate-50/80 dark:bg-slate-900/50 flex justify-end">
                    <Button variant="ghost" onClick={onClose} disabled={loading} className="font-bold text-slate-500 hover:text-slate-700">
                        Cerrar Ventana
                    </Button>
                </div>
            </div>
        </div>
    );
}
